import { motion } from 'framer-motion';
import ScrollRevealSection from './ScrollRevealSection';
import devMascot from '@/assets/dev-waving-new.webp';
import parentChildImg from '@/assets/parent-child.webp';
import careersGroupImg from '@/assets/careers-group.webp';
import { Lightbulb, TrendingUp, MessageCircle } from 'lucide-react';

// Apple-inspired easing
const appleEase: [number, number, number, number] = [0.22, 1, 0.36, 1];
function FeaturePoint({
  icon: Icon,
  title,
  description,
  index = 0
}: {icon: React.ElementType;title: string;description: string;index?: number;}) {
  return <motion.div className="group flex gap-4 items-start p-3 -ml-3 rounded-xl transition-all duration-500 hover:bg-accent/5" initial={{
    opacity: 0,
    x: -16
  }} whileInView={{
    opacity: 1,
    x: 0
  }} viewport={{
    once: false,
    amount: 0.6
  }} transition={{
    duration: 0.5,
    ease: appleEase,
    delay: index * 0.12
  }} whileHover={{
    x: 4,
    transition: {
      duration: 0.3,
      ease: appleEase
    }
  }}>
      {/* Icon with premium hover effect */}
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center transition-all duration-500 group-hover:bg-accent/20 group-hover:scale-105">
        <Icon className="w-5 h-5 text-accent transition-transform duration-500 group-hover:scale-110" />
      </div>
      <div>
        <h3 className="font-semibold text-foreground mb-1 transition-colors duration-300">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
      </div>
    </motion.div>;
}
export default function FeaturesSection() {
  return <>
      {/* Personalised Learning - Hints Only Philosophy */}
      <ScrollRevealSection eyebrow="Meet your tutor" headline="Hints, not handouts" description="Dev won't just give you the answer—because that's not learning. Instead, you'll get a gentle nudge, then a bigger hint, then step-by-step guidance. True understanding, earned your way." visual={<motion.div className="flex justify-center items-center h-full" whileHover={{
      scale: 1.02,
      transition: {
        duration: 0.5,
        ease: appleEase
      }
    }}>
            <motion.img alt="Dev - Your AI Study Buddy saying Hi I'm Dev" width={1024} height={1536} loading="lazy" decoding="async" className="w-64 md:w-80 lg:w-96 h-auto object-contain drop-shadow-xl mt-8" animate={{
        y: [0, -6, 0]
      }} transition={{
        duration: 5,
        ease: "easeInOut",
        repeat: Infinity,
        repeatType: "reverse"
      }} src="/lovable-uploads/e4cdfd94-134b-4130-aba3-fbe5eb352a95.webp" />
          </motion.div>} visualPosition="right" className="bg-muted">
        <div className="space-y-3 mt-6">
          <FeaturePoint index={0} icon={Lightbulb} title="The Hint Ladder" description="Gentle hints first, then stronger guidance—only revealing the answer if you truly need it." />
          <FeaturePoint index={1} icon={TrendingUp} title="Progress You Can See" description="Track mastery across every topic. Watch your confidence grow with each session." />
        </div>
      </ScrollRevealSection>

      {/* Parent Connection - Dinner Table Conversations */}
      <ScrollRevealSection eyebrow="For parents" headline="From report cards to real conversations" description="No more 'how was school?' dead ends. Get AI-powered conversation starters based on what your child is actually learning—perfect for the dinner table." visual={<motion.div className="relative rounded-3xl overflow-hidden shadow-2xl" whileHover={{
      scale: 1.02,
      transition: {
        duration: 0.5,
        ease: appleEase
      }
    }}>
            <img src={parentChildImg} alt="Parent helping child study" width={1024} height={1536} loading="lazy" decoding="async" className="w-full h-auto object-cover transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/15 to-transparent" />
          </motion.div>} visualPosition="right" className="bg-muted/30" showDoodlePattern>
        <div className="space-y-3 mt-6">
          <FeaturePoint index={0} icon={MessageCircle} title="Dinner Table Conversations" description="Get personalised questions to spark meaningful discussions about what they're learning." />
          <FeaturePoint index={1} icon={TrendingUp} title="Weekly Insights" description="Simple reports showing wins, gaps, and exactly what to focus on next—no jargon, just clarity." />
        </div>
      </ScrollRevealSection>

      {/* Career Pathways */}
      <ScrollRevealSection eyebrow="Your future" headline="Skills for every pathway" description="University, TAFE, apprenticeship, or straight to work—UnlockEd prepares you with real knowledge and confidence. Build skills that matter beyond the classroom." visual={<motion.div className="relative rounded-3xl overflow-hidden shadow-2xl" whileHover={{
      scale: 1.02,
      transition: {
        duration: 0.5,
        ease: appleEase
      }
    }}>
            <img src={careersGroupImg} alt="Diverse professionals representing various career pathways" width={1200} height={800} loading="lazy" decoding="async" className="w-full h-auto object-cover transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/15 to-transparent" />
          </motion.div>} visualPosition="right" className="bg-muted">
        <div className="space-y-3 mt-6">
          <FeaturePoint index={0} icon={TrendingUp} title="Career Pathway Suggestions" description="Get personalised suggestions for university, TAFE, apprenticeships, and careers based on your actual strengths and mastery data." />
          <FeaturePoint index={1} icon={MessageCircle} title="Skills Beyond the Exam" description="Build genuine knowledge that transfers to real-world careers — not just content memorised for a test." />
        </div>
      </ScrollRevealSection>
    </>;
}